import { useState, useMemo, useEffect } from "react";
import { Button, Checkbox, Input, message, Spin } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { BaseUrl } from "../../Constants/Constants";
import axios from "axios";
import dayjs from "dayjs";
import EventTable from "./EventTable";
import AddEventModal from "./AddEventModal";
import EditEventModal from "./EditEventModal";
import FilterModal from "./FilterModal";
import "./Event.css";

const CreateEvents = () => {
  const navigate = useNavigate();

  // State Management
  const [allEventsData, setAllEventsData] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  // Filters
  const [searchText, setSearchText] = useState("");
  const [filterDate, setFilterDate] = useState(null);
  const [filterAccessType, setFilterAccessType] = useState(null);
  const [filterMandatory, setFilterMandatory] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 4;

  // ✅ Get All Events
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${BaseUrl}event-management/get-all-events`,
          {
            withCredentials: true,
          }
        );

        if (res.data && res.data.data) {
          const apiEvents = res.data.data;

          // Group events by "main_name"
          const groupedEvents = Object.values(
            apiEvents.reduce((acc, ev) => {
              if (!acc[ev.main_name]) {
                acc[ev.main_name] = {
                  main_name: ev.main_name,
                  main_desc: ev.main_description,
                  mainInfo: { name: ev.main_name, desc: ev.main_description },
                  events: [],
                };
              }

              acc[ev.main_name].events.push({
                id: ev.event_management_id,
                topic: ev.topic,
                trainer: ev.trainer,
                start_date: ev.start_date,
                end_date: ev.end_date,
                timing: ev.timing,
                type: ev.type,
                link: ev.link,
                location: ev.location,
                course_id: ev.course_id,
                course_name: ev.course_name,
                access_type: ev.access_type,
                min_participants: ev.min_participants,
                max_participants: ev.max_participants,
                status: ev.status,
                mandatory: ev.mandatory,
              });

              return acc;
            }, {})
          );

          setAllEventsData(groupedEvents);
          message.success("Events loaded successfully!");
        }
      } catch (err) {
        console.error("Error fetching events:", err.message);
        message.error("Failed to load events. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // ✅ Filtering logic
const filteredEvents = useMemo(() => {
  const grouped = allEventsData.map((group) => {
    const visibleEvents = group.events.filter((ev) => {
      const matchesStatus = showInactive ? true : ev.status === "Open";
      
      const matchesSearch =
        ev.topic.toLowerCase().includes(searchText.toLowerCase()) ||
        group.main_name.toLowerCase().includes(searchText.toLowerCase()) ||
        ev.trainer.toLowerCase().includes(searchText.toLowerCase());
      
      const matchesDate = filterDate
        ? dayjs(ev.start_date).isSame(filterDate, "day") ||
          dayjs(ev.end_date).isSame(filterDate, "day")
        : true;
      
      const matchesAccess = filterAccessType
        ? ev.access_type === filterAccessType
        : true;
      
      // ✅ FIX: Handle both string ("Yes"/"No") and boolean (true/false) values
      const matchesMandatory = filterMandatory
        ? (ev.mandatory === filterMandatory || 
           (filterMandatory === "Yes" && ev.mandatory === true) ||
           (filterMandatory === "No" && ev.mandatory === false))
        : true;

      return (
        matchesStatus &&
        matchesSearch &&
        matchesDate &&
        matchesAccess &&
        matchesMandatory
      );
    });

    if (visibleEvents.length > 0) {
      return { ...group, events: visibleEvents };
    }
    return null;
  });

  const cleaned = grouped.filter(Boolean).flatMap((group) =>
    group.events.map((ev, idx) => ({
      key: `${group.main_name}-${idx}`,
      eventName: group.main_name,
      rowSpan: idx === 0 ? group.events.length : 0,
      checkedRowSpan: idx === 0 ? group.events.length : 0,
      topic: ev.topic,
      trainer: ev.trainer,
      start_date: ev.start_date,
      end_date: ev.end_date,
      timing: ev.timing,
      type: ev.type,
      location_link:
        ev.type === "virtual"
          ? ev.link
          : ev.type === "e-learning"
          ? ev.course_name || ev.course
          : ev.location,
      access_type: ev.access_type,
      participants:
        ev.access_type === "Open"
          ? "Open"
          : `Min: ${ev.min_participants ?? 0} - Max: ${
              ev.max_participants ?? 0
            }`,
      status: ev.status,
      mandatory: ev.mandatory,
    }))
  );

  return cleaned;
}, [
  allEventsData,
  searchText,
  filterDate,
  filterAccessType,
  filterMandatory,
  showInactive,
]);

  // ✅ Proceed with Selection
 const handleProceedWithSelection = () => {
    if (selectedGroups.length === 0) {
      message.warning("Please select at least one training to assign!");
      return;
    }

    const selectedEventData = allEventsData.filter((eventGroup) =>
      selectedGroups.includes(eventGroup.main_name)
    );

    // Extract all event IDs inside the group
    const eventIds = selectedEventData.flatMap((group) =>
      group.events.map((ev) => ev.id)
    );

    // ✅ Store in sessionStorage for persistence across page refreshes
    sessionStorage.setItem("selectedEventIds", JSON.stringify(eventIds));
    sessionStorage.setItem("selectedEvents", JSON.stringify(selectedEventData));

    navigate("/assignevents", {
      state: { selectedEvents: selectedEventData, eventIds },
    });
  };

  // ✅ Add Events
  const handleAddEvent = async (newEvent) => {
    const payload = {
      main_name: newEvent.mainInfo.name,
      main_desc: newEvent.mainInfo.description,
      events: newEvent.events.map((ev) => ({
        type: ev.type,
        topic: ev.topic,
        trainer: ev.trainer,
        start_date: ev.start_date,
        end_date: ev.end_date,
        timing: ev.timing,
        link: ev.link || "",
        location: ev.location || "",
        course_id: ev.courseData?.course_id || null,
        course_name: ev.courseData?.course_name || null,
        access_type: ev.access_type,
        min_participants: ev.min_participants
          ? Number(ev.min_participants)
          : null,
        max_participants: ev.max_participants
          ? Number(ev.max_participants)
          : null,
        status: ev.status || "Open",
        mandatory: ev.mandatory,
      })),
    };

    try {
      const res = await axios.post(
        `${BaseUrl}event-management/create-event`,
        payload,
        { withCredentials: true }
      );

      if (res.data?.statuscode === 200) {
        message.success("Event created successfully!");
        setIsModalOpen(false);

        // Optional: refresh event list
        axios
          .get(`${BaseUrl}event-management/get-all-events`, {
            withCredentials: true,
          })
          .then((response) => {
            if (response.data?.data) {
              const apiEvents = response.data.data;
              const groupedEvents = Object.values(
                apiEvents.reduce((acc, ev) => {
                  if (!acc[ev.main_name]) {
                    acc[ev.main_name] = {
                      main_name: ev.main_name,
                      main_desc: ev.main_description,
                      mainInfo: {
                        name: ev.main_name,
                        desc: ev.main_description,
                      },
                      events: [],
                    };
                  }

                  acc[ev.main_name].events.push({
                    id: ev.event_management_id,
                    topic: ev.topic,
                    trainer: ev.trainer,
                    start_date: ev.start_date,
                    end_date: ev.end_date,
                    timing: ev.timing,
                    type: ev.type,
                    link: ev.link,
                    location: ev.location,
                    course_id: ev.course_id,
                    course_name: ev.course_name,
                    access_type: ev.access_type,
                    min_participants: ev.min_participants,
                    max_participants: ev.max_participants,
                    status: ev.status,
                    mandatory: ev.mandatory,
                  });

                  return acc;
                }, {})
              );
              setAllEventsData(groupedEvents);
            }
          });
      } else {
        message.error("Failed to create event. Please try again.");
      }
    } catch (error) {
      console.error("Error creating event:", error);
      message.error("Error creating event. Please check console.");
    }
  };

  // ✅ Edit Form
  const handleEdit = (record) => {
    const group = allEventsData.find(
      (g) => g.mainInfo.name === record.eventName
    );
    const eventToEdit = group?.events.find((ev) => ev.topic === record.topic);

    if (eventToEdit) {
      setEditingEvent({
        ...eventToEdit,
        parentName: record.eventName,
        parentDescription: group.main_desc,
      });
      setIsEditModalOpen(true);
    }
  };

  // ✅ Update Events
  const handleUpdateEvent = async (updatedEvent) => {
    try {
      console.log("Updating Event ID:", updatedEvent.id);

      const payload = {
        main_name: updatedEvent.parentName,
        main_desc: updatedEvent.parentDescription,
        events: [
          {
            type: updatedEvent.type,
            topic: updatedEvent.topic,
            mandatory: updatedEvent.mandatory,
            trainer: updatedEvent.trainer,
            start_date: updatedEvent.start_date,
            end_date: updatedEvent.end_date,
            timing: updatedEvent.timing,
            link: updatedEvent.link || "",
            location: updatedEvent.location || "",
            course_id: updatedEvent.course_id || null,
            course_name: updatedEvent.course_name || null,
            access_type: updatedEvent.access_type,
            min_participants: updatedEvent.min_participants
              ? Number(updatedEvent.min_participants)
              : null,
            max_participants: updatedEvent.max_participants
              ? Number(updatedEvent.max_participants)
              : null,
            status: updatedEvent.status,
          },
        ],
      };

      const res = await axios.patch(
        `${BaseUrl}event-management/update-event/${updatedEvent.id}`,
        payload,
        { withCredentials: true }
      );

      if (res.data?.statuscode === 200) {
        message.success("Event updated successfully!");

        // Update local data
        setAllEventsData((prev) =>
          prev.map((grp) =>
            grp.mainInfo.name === updatedEvent.parentName
              ? {
                  ...grp,
                  events: grp.events.map((ev) =>
                    ev.id === updatedEvent.id
                      ? {
                          ...ev,
                          ...updatedEvent,
                          mandatory: updatedEvent.mandatory, 
                        }
                      : ev
                  ),
                }
              : grp
          )
        );
        setIsEditModalOpen(false);
      } else {
        message.error("Failed to update event.");
      }
    } catch (error) {
      console.error("Error updating event:", error);
      message.error("Error updating event. Please check console.");
    }
  };

  // ✅ Reset Filters
  const handleResetFilters = () => {
    setSearchText("");
    setFilterDate(null);
    setFilterAccessType(null);
    setFilterMandatory(null);
  };

  return (
    <div className="Users-main">
      <div className="addCourceandHeading">
        <h2>Manage Events</h2>
        { /* if need to show closed events then uncomeent this functionality is not written
        <Checkbox onChange={(e) => setShowInactive(e.target.checked)}>
          Show Closed Events
        </Checkbox> */}
      </div>

      {/* Top Action Bar */}
      <Spin spinning={loading} tip="Loading events...">
        <div
          className="top-action-bar"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "20px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {/* Left Section - Search & Filter */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <Input
              placeholder="Search by trainer, topic, or event name"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Button onClick={() => setIsFilterModalOpen(true)}>Filter</Button>
            <Button onClick={handleResetFilters}>Reset</Button>
          </div>

          {/* Right Section - Add & Proceed Buttons */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Button
              type="primary"
              className="ADD"
              onClick={() => setIsModalOpen(true)}
            >
              Add Event <CalendarOutlined />
            </Button>

            <Button
              type="primary"
              className="ADD"
              disabled={selectedGroups.length === 0}
              onClick={handleProceedWithSelection}
            >
              Proceed with Selection ({selectedGroups.length})
            </Button>
          </div>
        </div>

        {/* Modals */}
        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          filterDate={filterDate}
          setFilterDate={setFilterDate}
          filterAccessType={filterAccessType}
          setFilterAccessType={setFilterAccessType}
          filterMandatory={filterMandatory}
          setFilterMandatory={setFilterMandatory}
        />

        <AddEventModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleAddEvent}
        />

        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          editingEvent={editingEvent}
          onUpdate={handleUpdateEvent}
        />

        {/* Table */}
        <EventTable
          filteredEvents={filteredEvents}
          selectedGroups={selectedGroups}
          setSelectedGroups={setSelectedGroups}
          onEdit={handleEdit}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
        />
      </Spin>
    </div>
  );
};

export default CreateEvents;
